import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Send, Bot, User, Lightbulb } from 'lucide-react';
import { getPagePrompts } from '../utils/pagePrompts';
import MarkdownMessage from '../components/MarkdownMessage';
import styles from './SSULandingPage.module.css';
import FeatureHeader from '../components/FeatureHeader';

const SSUChatbotLanding = () => {
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isLimitReached, setIsLimitReached] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const MAX_INPUT_LENGTH = 2000;
  const [quickPrompts, setQuickPrompts] = useState([
    { text: "Loading...", icon: "/images/fluent_prompt-icon.svg" },
    { text: "Loading...", icon: "/images/fluent_prompt-icon.svg" },
    { text: "Loading...", icon: "/images/fluent_prompt-icon.svg" }
  ]);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const hasFetchedRef = useRef(false); // Prevent duplicate fetch in StrictMode

  // Debug messages state changes
  useEffect(() => {
    console.log('Messages state changed:', messages);
    console.log('hasMessages:', messages.length > 0);
  }, [messages]);



  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages]);

  // Separate function to refresh chat history
  const refreshChatHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('Refreshing chat history...');

      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(process.env.REACT_APP_API_BASE_URL + '/api/ssu/chatbot/messages/', {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        console.error('Failed to fetch chat history. Status:', response.status);
        return;
      }

      const data = await response.json();
      console.log('SSU API Response (refresh):', data);

      // Support both shapes:
      // 1) New shape with sessions/messages
      // 2) Legacy flat array of { id, role, message, timestamp }
      let formattedMessages = [];

      if (data && Array.isArray(data.sessions)) {
        console.log('Processing sessions format, sessions count:', data.sessions.length);
        // Flatten all sessions' messages, sort by timestamp ascending
        const sessions = data.sessions || [];
        const msgs = sessions
          .flatMap(s => {
            console.log(`Session ${s.id} has ${s.messages?.length || 0} messages`);
            return (s.messages || []).map(m => ({
              id: `${s.id}-${m.id}`,
              text: m.message,
              sender: m.role === 'user' ? 'user' : 'ai',
              timestamp: m.timestamp,
            }));;
          });
        formattedMessages = msgs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        console.log('Formatted messages from sessions:', formattedMessages);
      } else if (Array.isArray(data)) {
        console.log('Processing flat array format, items count:', data.length);
        formattedMessages = data.map(item => ({
          id: item.id,
          text: item.message,
          sender: item.role === 'user' ? 'user' : 'ai',
          timestamp: item.timestamp,
        }));
        console.log('Formatted messages from array:', formattedMessages);
      } else {
        console.log('Unknown data format:', typeof data, data);
      }

      console.log('Setting messages state with (refresh):', formattedMessages);
      setMessages(formattedMessages);
    } catch (error) {
      console.error('Error refreshing chat history:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      // Avoid double-invocation in React StrictMode
      if (hasFetchedRef.current) {
        console.log('Fetch already attempted, skipping...');
        return;
      }
      hasFetchedRef.current = true;
      console.log('Starting initial fetch chat history and page prompts...');

      // Fetch chat history
      await refreshChatHistory();
      
      // Fetch page prompts
      try {
        const prompts = await getPagePrompts(window.location.pathname);
        setQuickPrompts(prompts);
        console.log('Page prompts loaded:', prompts);
      } catch (error) {
        console.error('Error loading page prompts:', error);
        // Keep the loading placeholders if there's an error
      }
    };

    fetchData();

    return () => {
      // Reset the fetch flag when component unmounts so it can fetch again on remount
      hasFetchedRef.current = false;
    };
  }, []);

  const handleSendMessage = async () => {
    if (inputValue.trim() && !isLimitReached) {
      setErrorMessage('');
      const userPrompt = inputValue.trim();
      
      // Add user message to UI immediately
      const newMessage = { text: userPrompt, sender: 'user', id: Date.now() };
      setMessages(prev => [...prev, newMessage]);
      setInputValue('');
      setIsTyping(true);
      
      try {
        const token = localStorage.getItem('token');
        console.log('Sending prompt to SSU chatbot:', userPrompt);
        
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        
        // POST request to ask endpoint
        const response = await fetch(process.env.REACT_APP_API_BASE_URL + '/api/ssu/chatbot/ask/', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            prompt: userPrompt
          }),
        });
        
        let result = null;
        try {
          result = await response.json();
        } catch (err) {
          console.error('[SSU] Could not parse JSON from response:', err);
        }
        
        if (!response.ok) {
          const errorText =
            (result && result.message) ||
            (result && result.error) ||
            `Something went wrong (${response.status})`;

          if (response.status === 429) {
            setIsLimitReached(true);
            setErrorMessage(errorText);
          } else if (response.status === 400) {
            setErrorMessage(errorText);
          } else {
            setMessages(prev => [
              ...prev,
              {
                text: errorText,
                sender: 'system',
                id: Date.now() + 1,
              },
            ]);
            console.error('Failed to send message. Status:', response.status);
          }
          setIsTyping(false);
          return;
        }
        
        console.log('SSU chatbot response:', result);
        
        // Extract the assistant reply from the correct field
        const assistantMessage =
          result &&
          result.assistant_response &&
          result.assistant_response.message;

        if (assistantMessage) {
          // Show AI response immediately in the chat
          setMessages(prev => [
            ...prev,
            {
              text: assistantMessage,
              sender: 'ai',
              id: Date.now() + 1,
            },
          ]);
        } else {
          // Fallback: response parsed but field missing
          setMessages(prev => [
            ...prev,
            {
              text: '(No response received)',
              sender: 'ai',
              id: Date.now() + 1,
            },
          ]);
        }
        setIsTyping(false);

        // Also refresh chat history in the background for full sync
        refreshChatHistory();
        
      } catch (error) {
        console.error('Error sending message:', error);

        const errorText =
          (error.data && error.data.message) ||
          (error.data && error.data.error) ||
          (error.message ? error.message : 'Unable to reach the assistant right now. Please try again.');

        if (error.status === 429 || errorText.toLowerCase().includes('too many requests')) {
          setIsLimitReached(true);
          setErrorMessage(errorText);
        } else if (error.status === 400) {
          setErrorMessage(errorText);
        } else {
          setMessages(prev => [
            ...prev,
            {
              text: errorText,
              sender: 'system',
              id: Date.now(),
            },
          ]);
        }
        setIsTyping(false);
      }
    }
  };

  const handleQuickPrompt = (prompt) => {
    setInputValue(prompt.text);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const hasMessages = messages.length > 0;

  return (
    <div className={styles.chatbotLanding}>
      <div className={styles.contentContainer}>
        <div className={styles.headerSection}>
          <FeatureHeader
            src="/images/feature-headers/seller-university.png"
            alt="Suspire Seller University"
          />
        </div>

        {/* Welcome Panel - Fades when chat starts */}
        <div className={`${styles.welcomePanel} ${hasMessages ? styles.hidden : ''}`}>
          <h2 className={styles.mainHeading}>
            Welcome to Suspire Seller University
          </h2>
          <p className={styles.subtext}>
            Your AI-powered learning assistant for sustainable business growth
          </p>
          <p className={styles.commonPrompts}>
            Use one of the most common prompts below<br />
            or use your own to begin
          </p>
        </div>

        {/* Quick Prompts - Only show when no messages */}
        {!hasMessages && (
          <div className={styles.quickPromptsSection}>
            <div className={styles.promptsGrid}>
              {quickPrompts.map((prompt, index) => (
                <div 
                  key={index} 
                  className={styles.promptCard}
                  onClick={() => handleQuickPrompt(prompt)}
                >
                  <img src={prompt.icon} alt="prompt" className={styles.promptIcon} />
                  <p className={styles.promptText}>{prompt.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI Suggestion - Only show when no messages */}
        {!hasMessages && (
          <div className={styles.aiSuggestion}>
            <Lightbulb className={styles.suggestionIcon} />
            <span>AI-generated suggestion: "Would you like help navigating today?"</span>
          </div>
        )}

        {/* Chat Messages - Show when there are messages */}
        {hasMessages && (
          <div className={styles.chatSection}>
            <div className={styles.chatMessages} ref={chatContainerRef}>
              {messages.map((message) => (
                message.sender === 'system' ? (
                  <div key={message.id} className={styles.systemMessage}>
                    {message.text}
                  </div>
                ) : (
                  <div key={message.id} className={`${styles.message} ${styles[message.sender]}`}>
                    <div className={styles.messageContent}>
                      {message.sender === 'ai' && <Bot className={styles.messageIcon} />}
                      {message.sender === 'ai' ? (
                        <MarkdownMessage
                          className={`${styles.messageText} ${styles.markdown}`}
                          text={message.text}
                        />
                      ) : (
                        <div className={styles.messageText}>
                          {message.text}
                        </div>
                      )}
                      {message.sender === 'user' && <User className={styles.messageIcon} />}
                    </div>
                  </div>
                )
              ))}
              
              {/* Typing indicator */}
              {isTyping && (
                <div className={`${styles.message} ${styles.ai}`}>
                  <div className={styles.messageContent}>
                    <Bot className={styles.messageIcon} />
                    <div className={styles.typingIndicator}>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}

        {/* Input Section - Always at bottom */}
        <div className={styles.inputSection}>
          {errorMessage && (
            <div className={styles.errorBanner}>
              {errorMessage}
            </div>
          )}
          <div className={styles.inputContainer}>
            <MessageCircle className={styles.inputIcon} />
            <input
              type="text"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                if (errorMessage) setErrorMessage('');
              }}
              onKeyPress={handleKeyPress}
              placeholder="Ask whatever you want"
              className={styles.chatInput}
              disabled={isLimitReached}
              maxLength={MAX_INPUT_LENGTH}
            />
            <button 
              onClick={handleSendMessage}
              className={styles.sendButton}
              disabled={isLimitReached || !inputValue.trim()}
            >
              <Send className={styles.sendIcon} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SSUChatbotLanding;